package fr.manyverse.app.presentation.components

import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.gestures.detectTransformGestures
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.size
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.BrokenImage
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.LocalContentColor
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clipToBounds
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.PointerEventType
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.dp
import coil3.compose.SubcomposeAsyncImage
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.image_load_error
import org.jetbrains.compose.resources.stringResource

private const val MIN_ZOOM = 1f
private const val MAX_ZOOM = 5f
private const val DOUBLE_TAP_ZOOM = 2.5f

/**
 * Image clipped to its bounds, with pinch-to-zoom, drag-to-pan and mouse-wheel zoom (1×–5×).
 *
 * Double tap toggles between fit and zoomed: it is the always-available way back to the
 * fitted image, even when a pinch is hard to perform (small screen, image panned away).
 * Zoom is reset when the image or the container size changes (rotation, window resize),
 * and panning is bounded so the image can never leave the viewport.
 */
@Composable
fun ZoomableAsyncImage(
    imageUrl: String,
    contentDescription: String?,
    modifier: Modifier = Modifier
) {
    // Keyed on the URL so a new image never inherits the previous zoom state
    key(imageUrl) {
        var scale by remember { mutableFloatStateOf(MIN_ZOOM) }
        var offset by remember { mutableStateOf(Offset.Zero) }
        var containerSize by remember { mutableStateOf(IntSize.Zero) }

        fun applyTransform(newScale: Float, newOffset: Offset) {
            scale = newScale.coerceIn(MIN_ZOOM, MAX_ZOOM)
            offset = clampOffset(newOffset, scale, containerSize)
        }

        Box(
            modifier = modifier
                .clipToBounds()
                .onSizeChanged { size ->
                    if (size != containerSize) {
                        containerSize = size
                        applyTransform(MIN_ZOOM, Offset.Zero)
                    }
                }
                // Pinch-to-zoom + drag (touch on mobile, trackpad on desktop)
                .pointerInput(Unit) {
                    detectTransformGestures { _, pan, zoom, _ ->
                        applyTransform(scale * zoom, offset + pan)
                    }
                }
                .pointerInput(Unit) {
                    detectTapGestures(
                        onDoubleTap = { tap ->
                            if (scale > MIN_ZOOM) {
                                applyTransform(MIN_ZOOM, Offset.Zero)
                            } else {
                                // Keep the tapped point under the finger
                                val center = Offset(size.width / 2f, size.height / 2f)
                                applyTransform(DOUBLE_TAP_ZOOM, (tap - center) * (1f - DOUBLE_TAP_ZOOM))
                            }
                        }
                    )
                }
                // Scroll wheel zoom (mouse on desktop/web)
                .pointerInput(Unit) {
                    awaitPointerEventScope {
                        while (true) {
                            val event = awaitPointerEvent()
                            if (event.type == PointerEventType.Scroll) {
                                val delta = event.changes.firstOrNull()?.scrollDelta ?: continue
                                val zoomFactor = if (delta.y > 0) 0.9f else 1.1f
                                applyTransform(scale * zoomFactor, offset)
                                event.changes.forEach { it.consume() }
                            }
                        }
                    }
                },
            contentAlignment = Alignment.Center
        ) {
            SubcomposeAsyncImage(
                model = imageUrl,
                contentDescription = contentDescription,
                contentScale = ContentScale.Fit,
                modifier = Modifier
                    .fillMaxSize()
                    .graphicsLayer(
                        scaleX = scale,
                        scaleY = scale,
                        translationX = offset.x,
                        translationY = offset.y
                    ),
                // Sans ces états, un réseau lent ou une URL signée expirée laissent un écran vide
                loading = { CircularProgressIndicator(color = LocalContentColor.current) },
                error = {
                    Icon(
                        imageVector = Icons.Default.BrokenImage,
                        contentDescription = stringResource(Res.string.image_load_error),
                        modifier = Modifier.size(48.dp)
                    )
                }
            )
        }
    }
}

/**
 * Bounds the translation so the scaled image always covers the container center:
 * at 1× the offset is forced to zero, at N× it may move by (N - 1) / 2 of the container.
 */
private fun clampOffset(offset: Offset, scale: Float, containerSize: IntSize): Offset {
    val maxX = containerSize.width * (scale - 1f) / 2f
    val maxY = containerSize.height * (scale - 1f) / 2f
    return Offset(offset.x.coerceIn(-maxX, maxX), offset.y.coerceIn(-maxY, maxY))
}
